import _ from 'lodash';
import { useContext, useEffect, useRef, useState } from 'react';
import { faSync } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { accountSubCodeList } from '../../../../tableSettings';

import useFastStorage from '../../../../hooks/useFastStorage';
import useGlobalOkDialog from '../../../../hooks/useGlobalOkDialog';
import useFunction from '../../../../hooks/useFunction';

import ApplicationContext from '../../../../ApplicationContext';
import TextBox from '../../../../ui/TextBox';
import ClearableSearch from '../../../../uiControls/ClearableSearch';
import EditableTable, { CellData, EditableTableHandle, TableCellTypes } from '../../../../ui/tables/editing/EditableTable';
import { Portal } from 'react-portal';
import GreenButton from '../../../../ui/GreenButton';
import { JobNames } from '../../../../framework.core/JobNames';
import { SQLType } from '../../../../framework.core/data/SQLInfo';
import { CodeAccountSubListItem } from '../../../../fms.data/models/CodeAccountSubListItem';
import { Search } from '../../../../filtering';
import useSystemSettings from '../../../../hooks/useSystemSettings';
import { FmsSecTask } from '../../../../fms.core/constants';
import { checkTabIsDirty } from '../../ReferenceFiles';
import { findSecurityTask } from '../../../../util';
import postal from 'postal';
import BlackInverseButton from '../../../../ui/BlackInverseButton';
import constants from '../../../../constants';
import IFmsReferenceFilesContext from '../../IfmsReferenceFilesContext';
import CloseButton from '../../../../ui/CloseButton';
import useFunctionKeys from '../../../../hooks/useFunctionKeys';

function AccountSubCodeRenderer({data, row}: any) {
  const applyToLineItem = data[0].applyToLineItem;

  return (
    <TextBox
      textId={`ifms-account-subcode-${row.index}`}
      value={data[row.index].code}
      maxLength={20}
      onChange={e => {
        applyToLineItem(row.index, {code: e.target.value, isUpdate:true})
      }}
    />
  );
}

function DescriptionRenderer({data, row}: any) {
  const applyToLineItem = data[0].applyToLineItem;

  return (
    <TextBox
      textId={`ifms-account-description-${row.index}`}
      value={data[row.index].desc1}
      maxLength={100}
      onChange={e => {
        applyToLineItem(row.index, {desc1: e.target.value, isUpdate:true})
      }}
    />
  );
}

function formatElement(data: any) {
  return data;
}

function formatData(data: any) {
  return data.map((d: any) => formatElement(d));
}

function AccountSubCode() {
  useFunction({invokers: {
    ifmsSettingsSave: save,
    ifmsSettingsCancel: cancelOperation,
  }});
  useFunctionKeys({ onSave: handleSaveAndClose });

  const appContext = useContext(ApplicationContext);
  const refContext = useContext(IFmsReferenceFilesContext);
  const systemSettings = useSystemSettings();
  const [showGlobalOkDialog] = useGlobalOkDialog();

  //const tickRef = useRef(0);
  //const [, _setTick] = useState(0);

  const tableRef = useRef(null);
  const masterTableRef = useRef<EditableTableHandle>(null);
  const [loading, setLoading] = useState(false);
  const [allData, setAllData] = useState(Array<String>());
  const [//readyForEdit
    , setReadyForEdit] = useState(false);
  const [hasChanges, setHasChanges] = useFastStorage(`ifms-referenceFiles-haschanges`, false);
  const [data, _setData, removeData, ] = useFastStorage(`ifms-referenceFiles-accountSubCode-list`, []);
  const [orderBy, setOrderBy] = useFastStorage(`ifms-referenceFiles-accountSubCode-orderby`,{
    orderBy: 'code',
    orderByAscending: true,
    general1: 'code',
    pageSize: systemSettings.defaultPageSize,
    //generalText1: null,
  });
  const listSec = findSecurityTask(appContext, FmsSecTask.CodeAccountSubCode);
  const [focusFirstCell, setFocusFirstCell] = useState(true);

  const savedDataRef = useRef<Array<CodeAccountSubListItem>>([]);
  const oriData = useRef<CodeAccountSubListItem[]>([]);
 // const addingToTheFirstLine = useRef(false);

  const newIdRef = useRef(-1);

  const listItemType = 'CodeAccountSubListItem';

  useEffect(() => {
    setReadyForEdit(!loading)
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loading]
  );

  function handleSaveAndClose() {
    const saveBtn = document.getElementById('detail-save-button');
    if (saveBtn) {
      saveBtn.focus();
      const modals = document.querySelector('[class^="ReactModal__Content"]');
      if (!modals)
        saveBtn.click();
    }
  }

  function setData(d:any, callByFetchData: boolean = false) {
    setFocusFirstCell(callByFetchData);
    _setData(d, null, { forceClone: callByFetchData });
    setHasChanges(checkTabIsDirty(CodeAccountSubListItem, oriData.current, d));
  }

  function getNextReferenceId() {
    const result = newIdRef.current;
    newIdRef.current = newIdRef.current - 1;
    return result;
  }

  /**
  function setTick(tick: any) {
    tickRef.current = tick;
    _setTick(tick);
  }
   */

  const tableHeight = 520;

  async function fetchData(api: any, search: Search) {
    setLoading(true);
    // const apiResult = await api.getSettingsCodesAccountSubCodes();
    const apiResult = await api.getListItems(JobNames.SubGLCode,
      listItemType,
      CodeAccountSubListItem.calculateSearchParameters(search),
      1, search.pageSize);
    const apiResultforAll = await api.getListItems(JobNames.SubGLCode,
      listItemType,
      CodeAccountSubListItem.calculateSearchParameters(search),
      1);
    oriData.current = formatData(apiResult && apiResult.entities ? apiResult.entities : []);
    setData(formatData(apiResult && apiResult.entities ? apiResult.entities : []), true);
    savedDataRef.current = apiResult && apiResult.entities ? apiResult.entities : [];
    if(apiResultforAll && apiResultforAll.entities){
      setAllData(apiResultforAll.entities.map((a: CodeAccountSubListItem) => a.code.toLowerCase()));
    }

    setFocusFirstCell(false);
    setLoading(false);
    setHasChanges(false);
  }

  useEffect(() => {
    if (data.length === 0)
      reload();
    else {
      oriData.current = _.cloneDeep(data);
      savedDataRef.current = _.cloneDeep(data);
    }
    return () => {
      removeData();
    };
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  function reload(generalText:string = '') {
   setOrderBy({
    orderBy: 'code',
    orderByAscending: true,
    pageSize: systemSettings.defaultPageSize,
    generalText1: generalText,
    });
    fetchData(appContext.api, orderBy);
  }

  /*
  function onHeaderClick(header: any) {
    if (!header || !header.id || header.id === 'indicator' || !header.sortable) {
      return;
    }

    let newOrderBy = {...orderBy, orderByAscending: !orderBy.orderByAscending};

    if (header && header.id !== 'indicator') {
      if (header.id === orderBy.orderBy) {
        setOrderBy({ orderByAscending: !orderBy.orderByAscending });
      } else {
        setOrderBy({ orderBy: header.id, orderByAscending: true });
      }
    }
    sortingListData(CodeAccountSubListItem, data, setData, orderBy);
    // const sortResult = sortSettingListData(dataRef.current ?? [], newOrderBy, accountSubCodeList.columns);

    // if (sortResult.sorted) {
    //   setData(sortResult.data);
    // }
  }
*/
  function cancelOperation() {
    setHasChanges(false);
    removeData();
    return true;
  }

  async function save() {
    //const accountSubCodes = data.filter(x => x.id === 0 || x.hasBeenChanged);
    // const apiResult = await appContext.api.saveSettingsCodesAccountSubCodes(accountSubCodes);
    if (!data) {
      return;
    }

    let validData = data.filter((x:any) => x.code);

    let errMsg = '';
    const dataToSave = validData.filter((x: any) => x.id <= 0 || x.hasBeenChanged);
    const deleteData = savedDataRef.current.filter((x) => x.isDelete);
    if(dataToSave.length === 0 && deleteData.length === 0) {
      if (hasChanges) reload(orderBy.generalText1);
      return;
    }
    setLoading(true);

    for (let i = 0; i < deleteData.length; ++i) {
      const s = deleteData[i];
      const apiResult = await appContext.api.saveListItem(listItemType,
        SQLType.Delete, _.omit(s, ['isDelete']));
      if (!apiResult || !apiResult.success) {
        errMsg = apiResult?.message ?? constants.SAVE_ERROR_MESSAGE;
      }

    }
    if (dataToSave.length > 0) {
      const isDuplicate = await duplicateCell(dataToSave);
      if(isDuplicate){
        setLoading(false);
        return;
      }

      const apiResult = await appContext.api.saveList(listItemType, dataToSave);
      if (!apiResult || !apiResult.success) {
        errMsg += (errMsg ? '\n' : '') + (apiResult?.message ?? constants.SAVE_ERROR_MESSAGE);
      }
      else {
        dataToSave.forEach((item: any) => {
          const index = data.findIndex((d: any) => d.code === item.code);
          if (index >= 0) {
            data[index].id = item.newId;
            data[index].hasBeenChanged = false;
          }
          // 정상 저장된 경우 isNew Flag 제거
          if (item.isNew) {
            delete data[index].isNew;
          }
        });
      }
    }

    if (errMsg) {
      await showGlobalOkDialog(<p>{errMsg}</p>);
      setLoading(false);
      return;
    }

    // setData(data);
    // savedDataRef.current = _.cloneDeep(data);
    // oriData.current = _.cloneDeep(data);

    // setLoading(false);
    // setHasChanges(false);
    //cancelOperation();
    reload(orderBy.generalText1);

    await showGlobalOkDialog(<p>The data has been saved</p>);
  }

  function deleteRow(index: number) {
    if (!data) {
      return false;
    }

    const element = data[index];
    if (!element.isNew) {
      let deleteRow = savedDataRef.current.find(x => x.id === data[index].id);
      if (deleteRow) {
        deleteRow.isDelete = true;
      }
    }
    data.splice(index, 1);
    setData(data);

    const tempData = data.map((a: CodeAccountSubListItem) => a.code.toLowerCase());
    const newAllData = allData.splice(0, tempData.length, tempData);
    setAllData(newAllData);

    // setTick(tickRef.current + 1);
    return true;
  }

  function applyToLineItem(rowIndex: number, change: any) {
    if (!change) {
      return;
    }

    const newData = [...data];
    const checkData = data[rowIndex];
    if (!change.isNew && (change.code !== checkData.code || change.desc1 !== checkData.desc1)) {
      _.merge(newData[rowIndex], { ...change, hasBeenChanged: true });
      setData(newData);
      //setHasChanges(true);
      // setTick(tickRef.current + 1);
    }
  }

  // function addingLineToFirstLine(){
  //   addingToTheFirstLine.current = true;
  //   masterTableRef.current?.add();
  // }

  function addLineItem() {
    const newData = _.cloneDeep(data);

    return {
      setDataFunc: (list: any) => setData(list),
      curListData: newData,
      newData: { desc1: '', code: '', id: getNextReferenceId(), isNew: true, isCancel: true, isUpdate: false, isDelete: false }
    };
  }

  async function duplicateCell(checkData: any) {
    if (!data) {
      return;
    }

    let duplicateFieldText = '';

    let isDuplicate = false;
    let insList = checkData.filter((x: any) => x.isNew === true);
    let chgList = checkData.filter((x: any) => x.hasBeenChanged === true && !x.isNew);
    let focusRow = -1;

    if (insList.length > 0 && data.length > 0) {
      for (let i = 0; i < insList.length; i++) {
        let count = 0;
        let tempRow = -1;
        for (let j = 0; j < data.length; j++) {
          if (insList[i].code.toUpperCase() === data[j].code.toUpperCase()) {
            if (count === 0) { tempRow = j; }
            count++;
            if (count > 1) {
              if (focusRow === -1) focusRow = tempRow;
              break;
            }
          }
        }
        if (count > 1) {
          duplicateFieldText += `'${insList[i].code}' is a duplicate value.\r\n`;
          isDuplicate = true;
        }
      }
    }

    if (chgList.length > 0 && data.length > 0) {
      for (let i = 0; i < chgList.length; i++) {
        let count = 0;
        let tempRow = -1;
        for (let j = 0; j < data.length; j++) {
          if (chgList[i].code.toUpperCase() === data[j].code.toUpperCase()) {
            if (!data[j].isNew && data[j].hasBeenChanged === true && tempRow === -1) { tempRow = j; }
            count++;
            if (count > 1) {
              if (focusRow === -1) focusRow = tempRow;
              break;
            }
          }
        }
        if (count > 1) {
          duplicateFieldText += `'${chgList[i].code}' is a duplicate value.\r\n`;
          isDuplicate = true;
        }
      }
    }

    if (!isDuplicate) {
      const apiResult = await appContext.api.checkDuplicatedKeys(JobNames.SubGLCode, listItemType, insList, chgList);
      if (apiResult && apiResult.success && apiResult.message) {
        duplicateFieldText = `${apiResult.message}`;
        isDuplicate = true;
      } else if (!apiResult || !apiResult.success) {
        await showGlobalOkDialog('Could not check duplication.\r\nPlease try later.');
        return true;
      }
    }
    else
      await showGlobalOkDialog(`${duplicateFieldText}Please try after correcting duplication.`);

    return isDuplicate;
  }

  function getIndexOfFirstInvalidCell(rowIndex: number) {
    if (!data) {
      return undefined;
    }
    if (!data[rowIndex].code) {
      return 1;
    }
    if (allData) { //기존데이터 목록
      const newData = [...allData];
      if(newData.filter(x => x === data[rowIndex].code.toLowerCase()).length === 0) // 현재 목록에 없음
      {
        newData.splice(rowIndex, 1, data[rowIndex].code.toLowerCase());
        setAllData(newData);
        return undefined;
      }else{
        if(newData[rowIndex] === data[rowIndex].code.toLowerCase()) return undefined;
        else{
          showGlobalOkDialog(<p>'{data[rowIndex].code}' is a duplicate value.</p>);
          return 1;
        }
      }
    } 
    else {
      const newData = [data[rowIndex].code.toLowerCase()];
      setAllData(newData);
    }
    return undefined;
  }

  const accountSubCodeTableOptions = {
    customTableId: 'account-sub-code-list',
    ref: tableRef,
    selectable: false,
    tableName: 'account-sub-code-list',
    ...accountSubCodeList,
    height: `${tableHeight}px`,
    data: data.map((x: any, i: number) => ({...x, indicator: (i + 1).toString(), applyToLineItem})),
    rowRenderers: [
      { accessor: 'indicator' },
      { accessor: 'code', EditCell: AccountSubCodeRenderer },
      { accessor: 'desc1', EditCell: DescriptionRenderer },
    ],
    getCellData: (accessor: any, rowData: any, rowIndex: number): CellData | undefined => {
       if (accessor === 'code')
         return { type: TableCellTypes.Text, value: data[rowIndex].code };
       else if (accessor === 'desc1')
        return { type: TableCellTypes.Text, value: data[rowIndex].desc1 };
       else
         return undefined;
     },
     isCellLock: (accessor: string, rowData: any): boolean => {
      if(!(listSec?.grantAdd || listSec?.grantEdit || listSec?.grantDel))
        return true
      else
        return false;
     },
    //orderBy: [orderBy],
    //onHeaderClick,
    useEditControls: (listSec?.grantAdd || listSec?.grantEdit || listSec?.grantDel),
  };

  //const dataCount = data ? data.length : 0;
  const canBeEdit = !loading && (listSec?.grantAdd || listSec?.grantEdit);

  return (
    <div style={{marginTop: '40px'}}>
      {/* <Portal node={document && document.getElementById('settings-placeholder')}>
        <h1>Codes <FontAwesomeIcon icon={faAngleRight} /> Account Sub Code</h1>
        <div style={{width: '320px', fontWeight: 'bold'}}>
          <ClearableSearch initialValue={orderBy?.generalText1 || ''} onSearch={generalText1 => {
            setOrderBy({...orderBy, general1: generalText1 ? orderBy?.orderBy : null, generalText1});
            reload();
          }} />
        </div>
      </Portal> */}
      <div style={{ margin: 'auto' }}></div>
      <Portal node={document && document.getElementById('settings-command-placeholder')}>
        <div style={{display:'flex', marginTop:'5px'}}>
          <div className="ifms-common-bar-element">
            <GreenButton size="regular" disabled={!canBeEdit} onClick={save}>Save</GreenButton>
          </div>
          <div className="ifms-common-bar-element ifms-left-separator-light-gray">
            <CloseButton name='settings-close' mode='light' onClick={refContext.cancel} />
          </div>
          {/* <div className="ifms-common-bar-element">
            <BlackInverseButton name='codes-currency-refresh-button' size="large" style={{marginTop: '-5px'}} disabled={loading} onClick={() => { reload(orderBy.generalText1); }} data-tooltip="Refresh" >
              <FontAwesomeIcon color='#47C83E' icon={faSync} />
            </BlackInverseButton>
          </div> */}
        </div>
      </Portal>

      <div style={{ display: 'flex', width: '350px', marginTop: '10px' }}>
        <div style={{ width: '320px', fontSize: '11px', fontWeight: 'bold' }}>
          <ClearableSearch
            initialValue={orderBy?.generalText1 || ''}
            onSearch={async generalText1 => {
              await reload(generalText1 === null ? '' : generalText1);
            }}
          />
        </div>
        <div style={{ marginLeft: 'auto' }}></div>
        <div style={{ position: 'relative', top: '-4px' }}>
          {//<SecondaryButton name='codes-othershipment-type-add-button' size="regular" disabled={!listSec?.grantAdd} onClick={() => { addingLineToFirstLine() }}><FontAwesomeIcon icon={faPlus} />&nbsp; Add Line</SecondaryButton> &nbsp; 
          }
          <BlackInverseButton name='list-screen-refresh' size="large" disabled={loading} onClick={() => { reload(orderBy.generalText1); }} data-tooltip="Refresh" >
            <FontAwesomeIcon color='#47C83E' icon={faSync} />
          </BlackInverseButton>
        </div>
      </div>
      <div style={{ width: '350px' }}>
        <EditableTable
          initializeFocusedCell={focusFirstCell}
          ref={masterTableRef}
          tableOptions={accountSubCodeTableOptions}
          deleteLine={deleteRow}
          addLine={addLineItem}
          getIndexOfFirstInvalidCell={getIndexOfFirstInvalidCell}
          canDeleteRow={rowNumber => listSec?.grantDel ?? false}
        />
      </div>
    </div>
  );
}

export default AccountSubCode;
